/**
 * In-memory + localStorage cache of the latest fx_rates per currency.
 * Populated from Supabase on app boot and refreshed in the background.
 * Used by stampFx so writes use a live rate instead of the static table.
 */
import { supabase } from '@/integrations/supabase/client';
import { EXCHANGE_RATES, RATES_AS_OF } from './currencyConversion';

const STORAGE_KEY = 'wren:fx-rates-cache:v1';
const REFRESH_AFTER_MS = 12 * 60 * 60 * 1000; // 12h

export interface CachedRate {
  rate_to_eur: number;
  date: string;
  source: string;
}

interface CacheShape {
  cachedAt: number;
  rates: Record<string, CachedRate>;
}

let memory: CacheShape | null = null;

function readStorage(): CacheShape | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as CacheShape : null;
  } catch { return null; }
}

function writeStorage(cache: CacheShape) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cache)); } catch {/* quota */}
}

export function getCachedRate(currency: string): CachedRate | null {
  const code = (currency || 'EUR').toUpperCase();
  if (code === 'EUR') return { rate_to_eur: 1, date: new Date().toISOString().slice(0, 10), source: 'identity' };
  if (!memory) memory = readStorage();
  return memory?.rates[code] ?? null;
}

export async function refreshFxRatesCache(force = false): Promise<void> {
  if (!navigator.onLine) return;
  if (!memory) memory = readStorage();
  if (!force && memory && Date.now() - memory.cachedAt < REFRESH_AFTER_MS) return;

  // Pull the latest row per currency. fx_rates is small so a single fetch is fine.
  const { data, error } = await supabase
    .from('fx_rates')
    .select('currency, rate_to_eur, date, source')
    .order('date', { ascending: false })
    .limit(500);
  if (error || !data?.length) return;

  const rates: Record<string, CachedRate> = {};
  for (const row of data) {
    const code = (row.currency as string).toUpperCase();
    if (!rates[code]) {
      rates[code] = {
        rate_to_eur: Number(row.rate_to_eur),
        date: row.date as string,
        source: (row.source as string) ?? 'fx_rates',
      };
    }
  }
  memory = { cachedAt: Date.now(), rates };
  writeStorage(memory);
}

/**
 * Best static fallback — used if no cache and offline.
 */
export function getStaticFallbackRate(currency: string): CachedRate {
  const code = (currency || 'EUR').toUpperCase() as keyof typeof EXCHANGE_RATES;
  const rate = EXCHANGE_RATES[code] ?? 1;
  return { rate_to_eur: rate, date: RATES_AS_OF, source: `static_${RATES_AS_OF}` };
}